// src/components/admin/pages/BookOfTheWeek.jsx
import { useState, useEffect } from 'react';
import { Award, AlertCircle, CheckCircle, Plus, Pencil } from 'lucide-react';
import { bookOfTheWeekApi } from '../../../services/adminApi';
import ConfirmationModal from '../ConfirmationModal';
import FallbackBookModal from '../FallbackBookModal';
import BookSearchSelect from '../BookSearchSelect';
import AgeRangeField from '../AgeRangeField';
import { formatAgeRangeDraft, parseAgeRange, validateAgeRangeDraft } from '../../../utils/ageRange';

const emptyCreateForm = () => ({
  ageRangeDraft: parseAgeRange('3-5', { allowAllAges: true }),
  book_id: '',
  note: ''
});

const BookOfTheWeek = () => {
  const [records, setRecords] = useState([]);
  const [fallbackRecord, setFallbackRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [editing, setEditing] = useState(null); // { id, book_id, note, title }
  const [submitting, setSubmitting] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [showFallbackModal, setShowFallbackModal] = useState(false);

  useEffect(() => {
    loadRecords();
    loadFallbackRecord();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch every age group so nothing is hidden behind a fixed bucket.
      // Only the live universal fallback (age 'all' + active) is excluded here,
      // since it has its own section — everything else stays visible.
      const result = await bookOfTheWeekApi.getBookOfTheWeekRecords({ limit: 200 });
      setRecords((result.records || []).filter(r => !(r.age_group === 'all' && r.status === 'active')));
    } catch (err) {
      console.error('Failed to load Book of the Week records:', err);
      setError(err.message || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackRecord = async () => {
    try {
      const result = await bookOfTheWeekApi.getFallbackRecord();
      setFallbackRecord(result.fallback);
    } catch (err) {
      console.error('Failed to load fallback record:', err);
    }
  };

  const handleCreateRecord = async (e) => {
    e.preventDefault();
    // Age-range records only — the "all ages" bucket is the Universal Fallback's
    // own flow, so it is intentionally not offered here.
    const ageError = validateAgeRangeDraft(createForm.ageRangeDraft);
    if (ageError) {
      setError(ageError);
      return;
    }
    if (!createForm.book_id) {
      setError('Please search for and select a book.');
      return;
    }

    setSubmitting(true);
    try {
      await bookOfTheWeekApi.createBookOfTheWeekRecord({
        book_id: createForm.book_id,
        age_group: formatAgeRangeDraft(createForm.ageRangeDraft),
        note: createForm.note.trim() || null
      });
      setCreateForm(emptyCreateForm());
      setShowCreateForm(false);
      setError(null);
      await loadRecords();
    } catch (err) {
      console.error('Failed to create record:', err);
      setError(err.message || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDraft = async (e) => {
    e.preventDefault();
    if (!editing?.book_id) {
      setError('Please select a book.');
      return;
    }
    setSubmitting(true);
    try {
      await bookOfTheWeekApi.updateBookOfTheWeekRecord(editing.id, {
        book_id: parseInt(editing.book_id),
        note: editing.note?.trim() || null
      });
      setEditing(null);
      setError(null);
      await loadRecords();
    } catch (err) {
      console.error('Failed to update record:', err);
      setError(err.message || 'Failed to update record');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishRecord = (record) => {
    setConfirmationModal({
      title: 'Publish Book of the Week',
      message: `Publish "${record.title || 'this record'}" as Book of the Week for ages ${record.age_group}? This will make any existing active record for that age passive.`,
      confirmText: 'Publish',
      confirmType: 'primary',
      onConfirm: async () => {
        try {
          await bookOfTheWeekApi.publishBookOfTheWeekRecord(record.id);
          await loadRecords();
          setConfirmationModal(null);
        } catch (err) {
          setError(`Failed to publish record: ${err.message}`);
          setConfirmationModal(null);
        }
      }
    });
  };

  const handleMakePassive = (record) => {
    setConfirmationModal({
      title: 'Make Record Passive',
      message: `Make "${record.title || 'this record'}" passive? It will no longer be the active Book of the Week for ages ${record.age_group}.`,
      confirmText: 'Make Passive',
      confirmType: 'danger',
      onConfirm: async () => {
        try {
          await bookOfTheWeekApi.makeBookOfTheWeekRecordPassive(record.id);
          await loadRecords();
          setConfirmationModal(null);
        } catch (err) {
          setError(`Failed to make record passive: ${err.message}`);
          setConfirmationModal(null);
        }
      }
    });
  };

  const handleDeleteRecord = (record) => {
    setConfirmationModal({
      title: 'Delete Draft Record',
      message: `Delete draft record "${record.title || 'this record'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmType: 'danger',
      onConfirm: async () => {
        try {
          await bookOfTheWeekApi.deleteBookOfTheWeekRecord(record.id);
          await loadRecords();
          setConfirmationModal(null);
        } catch (err) {
          setError(`Failed to delete record: ${err.message}`);
          setConfirmationModal(null);
        }
      }
    });
  };

  const handleCreateFallback = async (data) => {
    try {
      await bookOfTheWeekApi.createFallbackRecord(data);
      await loadFallbackRecord();
    } catch (err) {
      setError(`Failed to create fallback record: ${err.message}`);
      throw err;
    }
  };

  const getStatusBadge = (status) => {
    const base = 'px-2 py-1 text-xs rounded-full';
    if (status === 'active') return `${base} bg-green-100 text-green-800`;
    if (status === 'passive') return `${base} bg-gray-100 text-gray-800`;
    return `${base} bg-yellow-100 text-yellow-800`;
  };

  // Group records by their age range for display.
  const groups = records.reduce((acc, record) => {
    (acc[record.age_group] = acc[record.age_group] || []).push(record);
    return acc;
  }, {});
  const sortedAgeGroups = Object.keys(groups).sort();

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Book of the Week</h1>
          <p className="text-text-secondary">One featured book per age range for Bukito&apos;s 0–8 audience.</p>
        </div>
        {!showCreateForm && !editing && (
          <button
            onClick={() => { setShowCreateForm(true); setError(null); }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Record</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <form onSubmit={handleCreateRecord} className="bg-bg-surface rounded-lg border border-gray-200 p-6 space-y-4">
          <h3 className="font-semibold text-text-primary">New Book of the Week draft</h3>
          <AgeRangeField
            label="Age Range"
            draft={createForm.ageRangeDraft}
            onChange={(ageRangeDraft) => setCreateForm(prev => ({ ...prev, ageRangeDraft }))}
            required
            disabled={submitting}
          />
          <BookSearchSelect
            label="Book"
            required
            selectedId={createForm.book_id}
            onSelect={(book) => setCreateForm(prev => ({ ...prev, book_id: book ? book.id : '' }))}
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Note</label>
            <textarea
              value={createForm.note}
              onChange={(e) => setCreateForm(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Optional note about this selection..."
              className="w-full border border-gray-200 rounded px-3 py-2 h-20 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create Draft'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreateForm(false); setCreateForm(emptyCreateForm()); setError(null); }}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Edit draft form */}
      {editing && (
        <form onSubmit={handleUpdateDraft} className="bg-bg-surface rounded-lg border border-primary-200 p-6 space-y-4">
          <h3 className="font-semibold text-text-primary">Edit draft — ages {editing.age_group}</h3>
          <BookSearchSelect
            label="Book"
            required
            selectedId={editing.book_id}
            selectedLabel={editing.title || ''}
            onSelect={(book) => setEditing(prev => ({ ...prev, book_id: book ? book.id : '' }))}
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Note</label>
            <textarea
              value={editing.note || ''}
              onChange={(e) => setEditing(prev => ({ ...prev, note: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-2 h-20 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setError(null); }}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading records...</p>
        </div>
      )}

      {!loading && sortedAgeGroups.length === 0 && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center text-text-secondary">
          <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No records yet</h3>
          <p>Create the first Book of the Week draft above.</p>
        </div>
      )}

      {/* Records grouped by age range */}
      {!loading && sortedAgeGroups.map((ageGroup) => {
        const groupRecords = groups[ageGroup];
        const active = groupRecords.find(r => r.status === 'active');
        const others = groupRecords.filter(r => r.status !== 'active');
        return (
          <div key={ageGroup} className="bg-bg-surface rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Ages {ageGroup}</h3>
              {active
                ? <span className={getStatusBadge('active')}>active selection set</span>
                : <span className={getStatusBadge('draft')}>no active selection</span>}
            </div>
            <div className="divide-y divide-gray-200">
              {[...(active ? [active] : []), ...others].map((record) => (
                <div key={record.id} className="p-4 flex items-start space-x-4">
                  <div className="w-14 h-20 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    {record.cover_url
                      ? <img src={record.cover_url} alt={record.title} className="w-full h-full object-cover rounded" />
                      : <span className="text-gray-500 text-xs">No Cover</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={getStatusBadge(record.status)}>{record.status}</span>
                      <h4 className="font-medium text-text-primary">{record.title || 'Unknown Title'}</h4>
                    </div>
                    <p className="text-text-secondary text-sm">by {record.author || 'Unknown Author'}</p>
                    {record.curator_note && (
                      <p className="text-sm text-text-secondary mt-1 italic">&ldquo;{record.curator_note}&rdquo;</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {record.status !== 'active' && (
                      <button onClick={() => handlePublishRecord(record)} className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                        Publish
                      </button>
                    )}
                    {record.status === 'active' && (
                      <button onClick={() => handleMakePassive(record)} className="px-3 py-1 text-red-600 border border-red-200 text-sm rounded hover:bg-red-50">
                        Make Passive
                      </button>
                    )}
                    {record.status === 'draft' && (
                      <>
                        <button
                          onClick={() => { setEditing({ id: record.id, age_group: record.age_group, book_id: record.book_id, note: record.curator_note || '', title: record.title }); setShowCreateForm(false); setError(null); }}
                          className="px-3 py-1 border border-gray-200 text-text-secondary text-sm rounded hover:bg-gray-50 flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={() => handleDeleteRecord(record)} className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Universal fallback */}
      <div className="bg-bg-surface rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-text-primary">Universal Fallback</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-text-primary">Fallback Record (All Ages)</span>
              <p className="text-sm text-text-secondary mt-1">
                Used when no age range has an active Book of the Week.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {fallbackRecord ? (
                <><CheckCircle className="w-5 h-5 text-green-500" /><span className="text-green-600 font-medium">Active</span></>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-600 font-medium">Missing</span>
                  <button onClick={() => setShowFallbackModal(true)} className="ml-3 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                    Create Fallback
                  </button>
                </>
              )}
            </div>
          </div>
          {fallbackRecord && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-start space-x-3">
              <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                {fallbackRecord.cover_url
                  ? <img src={fallbackRecord.cover_url} alt={fallbackRecord.title} className="w-full h-full object-cover rounded" />
                  : <span className="text-gray-500 text-xs">No Cover</span>}
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-text-primary">{fallbackRecord.title || 'Unknown Title'}</h4>
                <p className="text-text-secondary text-sm">by {fallbackRecord.author || 'Unknown Author'}</p>
                <button onClick={() => setShowFallbackModal(true)} className="text-sm text-primary-600 hover:underline mt-1">
                  Replace fallback
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmationModal && (
        <ConfirmationModal
          isOpen={true}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmText={confirmationModal.confirmText}
          confirmType={confirmationModal.confirmType}
          onClose={() => setConfirmationModal(null)}
          onConfirm={confirmationModal.onConfirm}
        />
      )}

      <FallbackBookModal
        isOpen={showFallbackModal}
        onClose={() => setShowFallbackModal(false)}
        onSuccess={handleCreateFallback}
      />
    </div>
  );
};

export default BookOfTheWeek;
